import { useLazyQuery, useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import useUserStore from "../store/userStore";
import { useEffect, useMemo } from "react";
import LoadingMetro from "../components/LoadingMetro";
import useConsentStore from "../store/consentStore";
import ErrorLayout from "../components/layout/ErrorLayout";
import type { ConsentOnlineItem, Company } from "../utils/Types/ConsentTypes";

const GET_ME = gql`
  query {
    me {
      id
      email
      username
      locale
      authorized
      signCode
      roles {
        name
      }
      mainEmployment {
        id
      }
    }
  }
`;

// Queries for multiple companies consent support
export const GET_CONSENTS_ONLINE = gql`
  query consentsOnline {
    consentsOnline {
      companyName
      ico
      email
      phone
      createdAt
      deletedAt
      createdById
      emailVerifyCodeExpires
      emailVerifiedState
      text {
        ico
        id
        text
        type
        validTo
      }
    }
  }
`;

export const GET_CONSENTS_OWN_HANDS = gql`
  query consentsOwnHands {
    consentsOwnHands {
      companyName
      type
      createdAt
      deletedAt
      deletedById
      text {
        ico
        id
        text
        type
        validTo
      }
    }
  }
`;

export const GET_CONSENT_TEXTS_BY_ICOS = gql`
  query consentTextsByIcos($icos: [String!]!) {
    consentTextsByIcos(icos: $icos) {
      companyName
      ico
      texts {
        id
        text
        type
        validTo
      }
    }
  }
`;

// Legacy queries (for backward compatibility)
const GET_CONSENT_ONLINE = gql`
  query consentOnline {
    consentOnline {
      ico
      email
      phone
      createdAt
      deletedAt
      createdById
      text {
        id
        text
        type
        validTo
      }
      emailVerifyCodeExpires
      emailVerifiedState
    }
  }
`;

const GET_CONSENT_OWN_HANDS = gql`
  query {
    consentOwnHands {
      id
      type
      createdAt
      deletedAt
      deletedById
      text {
        id
        text
        type
        validTo
      }
    }
  }
`;

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { loading, data } = useQuery(GET_ME);
  const location = useLocation();
  const {
    setUser,
  } = useUserStore();

  useEffect(() => {
    if (data?.me) {
      setUser(data.me);
    }
  }, [data, setUser]);

  if (loading) {
    return <LoadingMetro />;
  }

  if (!loading && !data?.me) {
    return (
      <Navigate
        to={`/login/external?redirectTo=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }

  return children;
};

export const ConsentGuard = ({ children }: { children: React.ReactNode }) => {
  const { data, loading } = useQuery(GET_ME);

  // Queries for multiple companies
  const { data: consentsOnlineData, loading: consentsOnlineLoading } = useQuery(GET_CONSENTS_ONLINE);
  const { data: consentsOwnHandsData, loading: consentsOwnHandsLoading } = useQuery(GET_CONSENTS_OWN_HANDS);
  const [fetchConsentTexts, { data: consentTextsData }] = useLazyQuery(GET_CONSENT_TEXTS_BY_ICOS);

  const { setUser } = useUserStore();

  const {
    setConsentDialogOpen,
    setCompanies,
    setConsentsOnline,
    setConsentsOwnHands,
    setConsentTextsByIcos,
  } = useConsentStore();

  const navigate = useNavigate();
  const location = useLocation();

  // Set user data
  useEffect(() => {
    if (data?.me) {
      setUser(data.me);
    }
  }, [data, setUser]);

  // Check if user needs to fill consent form (redirect to /consent)
  useEffect(() => {
    if (!loading && !consentsOwnHandsLoading && !consentsOnlineLoading && data?.me) {
      // Check if user has any consent online data
      const consentsOnline = consentsOnlineData?.consentsOnline || [];
      if (consentsOnline.length === 0) {
        return;
      }

      // Get all unique icos from consents online
      const onlineIcos = new Set(
        consentsOnline.map((c: ConsentOnlineItem) => c.ico).filter(Boolean)
      );

      // Get icos that have consent own hands with type set
      const ownHandsIcos = new Set(
        (consentsOwnHandsData?.consentsOwnHands || [])
          .filter((c: { type?: string; text?: { ico?: string } }) => c.type)
          .map((c: { text?: { ico?: string } }) => c.text?.ico)
          .filter(Boolean)
      );

      // Check if all companies have consent own hands
      const missingConsentIcos = Array.from(onlineIcos).filter(ico => !ownHandsIcos.has(ico));

      if (missingConsentIcos.length > 0) {
        // Skip redirect if already on consent page with ico param
        const currentParams = new URLSearchParams(location.search);
        const currentIco = currentParams.get('ico');
        const hasVerifyEmail = currentParams.has('verifyEmail');
        if (location.pathname === '/consent' && currentIco) {
          return;
        }
        // Redirect to consent page with first missing ico, preserving existing params
        const firstMissingIco = missingConsentIcos[0] as string;
        const existingParams = new URLSearchParams(location.search);
        existingParams.set('ico', firstMissingIco);
        // Don't show consent dialog if user is verifying email
        if (!hasVerifyEmail) {
          setConsentDialogOpen(true);
        }
        navigate(`/consent?${existingParams.toString()}`);
      }
    }
  }, [
    data,
    loading,
    consentsOwnHandsData,
    consentsOwnHandsLoading,
    consentsOnlineData,
    consentsOnlineLoading,
    setConsentDialogOpen,
    navigate,
    location.pathname,
    location.search,
  ]);

  // Extract unique companies from consentsOnline (consentsOwnHands doesn't have ico)
  const companies = useMemo(() => {
    const companiesMap = new Map<string, Company>();

    consentsOnlineData?.consentsOnline?.forEach((consent: ConsentOnlineItem) => {
      if (consent.ico && !companiesMap.has(consent.ico)) {
        companiesMap.set(consent.ico, {
          ico: consent.ico,
          companyName: consent.companyName,
        });
      }
    });

    return Array.from(companiesMap.values());
  }, [consentsOnlineData]);

  // Save multiple consents data to store
  useEffect(() => {
    if (companies.length > 0) {
      setCompanies(companies);
    }
  }, [companies, setCompanies]);

  useEffect(() => {
    if (consentsOnlineData?.consentsOnline) {
      setConsentsOnline(consentsOnlineData.consentsOnline);
    }
  }, [consentsOnlineData, setConsentsOnline]);

  useEffect(() => {
    if (consentsOwnHandsData?.consentsOwnHands) {
      setConsentsOwnHands(consentsOwnHandsData.consentsOwnHands);
    }
  }, [consentsOwnHandsData, setConsentsOwnHands]);

  // Load texts for all companies
  useEffect(() => {
    if (companies.length > 0) {
      const icos = companies.map(c => c.ico);
      fetchConsentTexts({ variables: { icos } });
    }
  }, [companies, fetchConsentTexts]);

  useEffect(() => {
    if (consentTextsData?.consentTextsByIcos) {
      setConsentTextsByIcos(consentTextsData.consentTextsByIcos);
    }
  }, [consentTextsData, setConsentTextsByIcos]);

  return children;
};

export const AuthRedirect = () => {
  const { data, loading, error } = useQuery(GET_ME);
  const {
    setUser,
  } = useUserStore();

  useEffect(() => {
    if (data?.me) {
      setUser(data.me);
    }
  }, [data, setUser]);

  if (loading) {
    return <LoadingMetro />;
  }

  if (error) return <ErrorLayout errorMsg={error.message || ""} />;

  if (!loading && data.me) return <Navigate to="/" replace />;

  return null;
};
